<script setup>
/**
 * FormSaveBtn
 * Triggers the save action for a specific form via Vuex.
 * Manages button feedback (warning color if modified) and handles
 * post-save navigation logic based on the table name and current route.
 */
import { computed, getCurrentInstance } from 'vue';
import Button from "../Button.vue";

const props = defineProps({
    formId: {
        type: String,
        required: true
    },
    buttonText: String,
    // cancelOnSave: used to suppress the component's internal onSave redirection logic
    cancelOnSave: Boolean,
    defaultBtnClass: String
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const currentData = computed(() => formState.value?.currentData || {});

const isModified = computed(() => store.getters[`${props.formId}/isModified`]?.());

const isSaving = computed(() => formState.value?.isSaving);

const tableName = computed(() => formState.value?.tableName);

const hide = computed(() => tableName.value === 'files');

// --- Methods ---

/**
 * Handles redirection and state updates after a successful save.
 */
const onSave = (item) => {
    if (tableName.value?.indexOf('product_variations__') === 0) {
        window.location = `/#/${tableName.value}?productId=${currentData.value.productId}`;
    }
    // If we just saved a brand new item, redirect to its specific edit form or update context
    else if (formState.value?.id === 0 && item.id) {
        const routeName = proxy.$route.name;

        switch (true) {
            case routeName === 'componentFields':
                store.dispatch('componentFields/setItems');
                proxy.$router.push({ params: { fieldId: item.id } });
                break;

            case (routeName === 'themesEditor' && proxy.$route.query.section === 'content_types'):
                store.dispatch('componentFields/setItems');
                store.commit('componentFields/activeEditId', item.id);
                break;

            default:
                proxy.$router.push({
                    name: 'form',
                    params: { id: item.id },
                    query: proxy.$route.query
                });
        }
    }
};

const onClick = () => {
    store.dispatch(`${props.formId}/save`)
        .then(res => {
            // Check for invalid flag returned by the store's validation logic
            if (!res?.invalid && !props.cancelOnSave) {
                onSave(res);
            }
        });
};
</script>

<template>
    <Button
        v-if="!hide"
        :disabled="isSaving"
        title="Save"
        :default-class="defaultBtnClass"
        :active-class="'btn-warning'"
        :active-value="isModified"
        @click.native="onClick"
    >
        <span v-if="buttonText" v-html="buttonText"/>
        <span v-else class="bi-save"/>
    </Button>
</template>